// =============================================================================
// Priority Spel V3 — Minigame registry
// -----------------------------------------------------------------------------
// The single, explicit source of truth mapping a `meta.id` to its game adapter.
// The shell (views/GameView.vue) selects the game ONLY through this registry —
// there are no game-specific if/switch branches in the shell, and ids are never
// turned into arbitrary import paths.
//
// Games are lazy-loaded (each adapter + its vendored game is its own chunk) with
// a shared loading state; load failures surface to the host, which shows a safe
// fallback (see GameView.vue).
//
// Adding a fifth game (no shell changes needed):
//   1. Add the game under src/games/vendor/<id>/ and a thin adapter in
//      src/games/adapters/<id>.vue (translate its events to finished/quit).
//   2. Register it with the correct meta.id in GAME_LOADERS below.
//   3. Add its meta (incl. starRules) to config/gameConfig.js MINIGAMES, plus its
//      reel art (views/HomeView.vue SEGMENT), documentation and tests.
// =============================================================================
import { defineAsyncComponent } from 'vue'
import GameLoading from './GameLoading.vue'

// meta.id → lazy import of the adapter. The keys are the authoritative supported
// ids and MUST match config/gameConfig.js MINIGAMES ids.
const GAME_LOADERS = {
  match3: () => import('./adapters/Match3Adapter.vue'), // "Sterren swipen" (match-3, score)
  unblock: () => import('./adapters/UnblockAdapter.vue'), // "Voetbal puzzelblok" (slide, attempts)
  timing: () => import('./adapters/TimingAdapter.vue'), // "Sterrenvangen Ruimte" (catch, score)
  wordle: () => import('./adapters/WordleAdapter.vue'), // "Lingo" (word guess, attempts)
}

/** @returns {boolean} whether `id` is an explicitly registered, supported game. */
export function isSupportedGame(id) {
  return typeof id === 'string' && Object.prototype.hasOwnProperty.call(GAME_LOADERS, id)
}

/** @returns {string[]} all registered game ids. */
export function supportedGameIds() {
  return Object.keys(GAME_LOADERS)
}

/** @returns {(() => Promise<unknown>) | null} the raw lazy-import factory, or null. */
export function gameLoader(id) {
  return isSupportedGame(id) ? GAME_LOADERS[id] : null
}

/**
 * Build a lazily-loaded async component for `id`, with a loading state. Returns
 * null when `id` is not supported (the host then shows the "not available"
 * fallback instead of loading anything). A load failure (or timeout) throws and
 * is caught by the host's errorCaptured hook, which shows a safe load-error
 * fallback with a retry.
 *
 * @param {string} id
 * @returns {import('vue').Component | null}
 */
export function createGameComponent(id) {
  const loader = gameLoader(id)
  if (!loader) return null
  return defineAsyncComponent({
    loader,
    loadingComponent: GameLoading,
    delay: 150,
    timeout: 20000,
  })
}
